import { useState, useContext } from 'react'
import { SnackbarContext } from 'contexts/SnackbarContext'
import { axios } from 'api'
import { LoadingContext } from 'contexts/LoadingContext'
import { downloadPdfFromBase64 } from 'utils/downloadPdfFromBase64'
import { useTranslation } from 'react-i18next'

export type OrderItem = {
  id: string
  productId: string
  sku: string
  color: string
  unitPrice: string
  quantity: number
  isAssembled: boolean
  hingeDirection: string
  imageIndex: string
}

export type Order = {
  id: string
  status: string
  totalAmount: number
  discountAmount: number
  taxAmount: number
  assemblyFee: number
  finalAmount: number
  poNumber: string
  salesOrderNumber: string
  createdAt: string
  OrderItems: OrderItem[]
  transactionDetails?: {
    transactionId: string
    amount: number
    cardType: string
    cardNumber: string
    billingAddress: BillingAddress
    date: string
  }
}

export type BillingAddress = {
  firstName: string
  lastName: string
  address: string
  city: string
  state: string
  zipCode: string
  country: string
}

const useOrders = () => {
  const { t } = useTranslation()
  const [orders, setOrders] = useState<Order[]>([])
  const [totalPages, setTotalPages] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isLoadingOrders, setIsLoadingOrders] = useState<boolean>(true)
  const { showMessage } = useContext(SnackbarContext)
  const { showLoading, stopLoading } = useContext(LoadingContext)

  const fetchOrders = async (page: number) => {
    setIsLoadingOrders(true)
    const response = await axios.get(`/orders?page=${page}`)
    if (response.status === 200) {
      setOrders(response.data.orders)
      setTotalPages(response.data.totalPages)
      setCurrentPage(response.data.currentPage)
    } else {
      showMessage(t('snackbar.order.fetchFailed'), 'error')
    }
    setIsLoadingOrders(false)
  }

  const downloadInvoice = async (orderId: string, salesOrderNumber: string) => {
    showLoading()
    const response = await axios.get(`/orders/${orderId}/download`)
    const pdfBase64 = response.data.pdf
    if (pdfBase64) {
      downloadPdfFromBase64(pdfBase64, `Invoice_${salesOrderNumber}.pdf`)
    } else {
      const errorMessage =
        response?.data?.error || t('snackbar.general.unexpectedError')
      showMessage(errorMessage, 'error')
    }

    stopLoading()
  }

  return {
    orders,
    totalPages,
    currentPage,
    setCurrentPage,
    fetchOrders,
    downloadInvoice,
    isLoadingOrders
  }
}

export default useOrders
